import type React from "react";
import { useMemo, useState } from "react";
import { ArrowDownWideNarrow, ArrowUpWideNarrow, Search, X } from "lucide-react";
import type { reportingPeriod } from "@duress/shared";
import { Input } from "@/components/ui/input";
import { BRAND, PILL_TEXT } from "@/lib/colors";
import { cn } from "@/lib/utils";

type TeamDailyActivity = reportingPeriod.TeamDailyActivity;
type DailyActivityTier = reportingPeriod.DailyActivityTier;
type DailyActivityDay = reportingPeriod.DailyActivityDay;

const TIER_LABELS: Record<DailyActivityTier, string> = {
  highly_active: "Highly Active",
  occasional: "Active",
  rare: "Intermittent",
  dark: "Inactive",
};

// Fill colour for pill backgrounds and the filter-dot indicator.
const TIER_COLORS: Record<DailyActivityTier, string> = {
  highly_active: BRAND.green,
  occasional: BRAND.blue,
  rare: BRAND.orange,
  dark: BRAND.red,
};

// Text colour for the pill — distinct from the fill above because the raw
// BRAND hex fails AA contrast as text in one theme or the other. See
// PILL_TEXT in lib/colors.ts.
const TIER_TEXT: Record<DailyActivityTier, string> = {
  highly_active: PILL_TEXT.green,
  occasional: PILL_TEXT.blue,
  rare: PILL_TEXT.orange,
  dark: PILL_TEXT.red,
};

// Worst-to-best, matching the default sort direction.
const TIER_ORDER: DailyActivityTier[] = ["dark", "rare", "occasional", "highly_active"];

type SortDir = "worst-first" | "best-first";

function TierFilterPill({
  label,
  count,
  color,
  active,
  onClick,
}: {
  label: string;
  count: number;
  color: string | null;
  active: boolean;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-head font-medium transition-colors
        ${
          active
            ? "bg-phoenix-orange text-white border-phoenix-orange"
            : "bg-card text-muted-foreground border-border hover:text-foreground hover:border-phoenix-orange"
        }`}
    >
      {color && (
        <span
          className="h-1.5 w-1.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: active ? "white" : color }}
        />
      )}
      {label}
      <span className={active ? "text-white/70" : "text-muted-foreground/70"}>{count}</span>
    </button>
  );
}

function TierPill({ tier }: { tier: DailyActivityTier }): React.JSX.Element {
  const fill = TIER_COLORS[tier];
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-head font-semibold whitespace-nowrap"
      style={{ backgroundColor: `${fill}1a`, color: TIER_TEXT[tier] }}
    >
      {TIER_LABELS[tier]}
    </span>
  );
}

const SHORT_MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function formatDayLabel(date: string): string {
  const [, m, d] = date.split("-");
  const month = SHORT_MONTHS[parseInt(m ?? "1", 10) - 1] ?? "";
  return `${parseInt(d ?? "1", 10)} ${month}`;
}

function ActivityHeatmap({ days }: { days: DailyActivityDay[] }): React.JSX.Element {
  const activeCount = days.filter((d) => d.active).length;
  return (
    <div
      className="flex flex-wrap gap-[3px]"
      role="img"
      aria-label={`Daily activity — active on ${activeCount} of ${days.length} days`}
    >
      {days.map((d) => (
        <div
          key={d.date}
          title={`${d.date} — ${d.active ? "at least one qualifying session" : "no qualifying session"}`}
          className="h-3 w-3 rounded-[2px]"
          style={{ backgroundColor: d.active ? BRAND.green : "hsl(var(--muted))" }}
        />
      ))}
    </div>
  );
}

// Rolling 7-day active-day count, one point per day once a full trailing
// 7-day window exists within the month (so the first 6 days of the month
// have no point — there's no prior-month data to look back into).
function rollingSevenDayTrend(days: DailyActivityDay[]): number[] {
  const points: number[] = [];
  for (let i = 6; i < days.length; i++) {
    let count = 0;
    for (let j = i - 6; j <= i; j++) {
      if (days[j]?.active) count += 1;
    }
    points.push(count);
  }
  return points;
}

function TrendSparkline({ values }: { values: number[] }): React.JSX.Element | null {
  if (values.length < 2) return null;

  const width = 200;
  const height = 32;
  const maxValue = 7;
  const stepX = width / (values.length - 1);
  const points = values
    .map((v, i) => `${(i * stepX).toFixed(1)},${(height - (v / maxValue) * height).toFixed(1)}`)
    .join(" ");

  return (
    <svg
      viewBox={`0 0 ${String(width)} ${String(height)}`}
      preserveAspectRatio="none"
      className="h-8 w-full"
      role="img"
      aria-label="Rolling 7-day active-day trend"
    >
      <polyline
        points={points}
        fill="none"
        stroke="hsl(var(--muted-foreground))"
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function TeamActivityCard({
  team,
  selected,
  onToggleCompare,
}: {
  team: TeamDailyActivity;
  selected: boolean;
  onToggleCompare: () => void;
}): React.JSX.Element {
  const firstDay = team.days[0];
  const lastDay = team.days[team.days.length - 1];
  const l7Color = team.l7 > 0 ? PILL_TEXT.green : "inherit";
  const trend = rollingSevenDayTrend(team.days);

  return (
    <div className={cn("card-duress p-4", selected && "ring-2 ring-phoenix-orange")}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-head font-semibold text-foreground truncate">{team.store}</h3>
          <p className="text-xs text-muted-foreground">
            {team.devicesTotal} device{team.devicesTotal === 1 ? "" : "s"}
          </p>
        </div>
        <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground whitespace-nowrap flex-shrink-0 cursor-pointer hover:text-foreground">
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleCompare}
            className="h-3.5 w-3.5 rounded border-border accent-phoenix-orange"
          />
          Compare
        </label>
      </div>

      <div className="mt-3 flex items-end gap-6">
        <div>
          <div className="num text-[26px] leading-none font-semibold" style={{ color: l7Color }}>
            {team.l7}/7
          </div>
          <p className="text-xs text-muted-foreground mt-1">L7 — days active</p>
        </div>
        <div>
          <div className="num text-[26px] leading-none font-semibold">{team.l28}/28</div>
          <p className="text-xs text-muted-foreground mt-1">L28 — days active</p>
        </div>
        <TierPill tier={team.tier} />
      </div>

      {firstDay && lastDay && (
        <div className="mt-4">
          <p className="text-xs text-muted-foreground mb-1.5">
            Daily activity, {formatDayLabel(firstDay.date)} – {formatDayLabel(lastDay.date)}
          </p>
          <ActivityHeatmap days={team.days} />
        </div>
      )}

      {trend.length >= 2 && (
        <div className="mt-4">
          <p className="text-xs text-muted-foreground mb-1.5">Rolling 7-day trend</p>
          <TrendSparkline values={trend} />
        </div>
      )}
    </div>
  );
}

// ── Pure presentational component ────────────────────────────────────────────

export interface TeamActivityProps {
  data: reportingPeriod.DailyActivityResponse;
}

export function TeamActivity({ data }: TeamActivityProps): React.JSX.Element {
  const [query, setQuery] = useState("");
  // Up to two tiers can be selected at once — selecting a second tier
  // switches the view into a side-by-side comparison of both groups.
  const [selectedTiers, setSelectedTiers] = useState<DailyActivityTier[]>([]);
  // Worst-first by default, matching Team Performance's convention of
  // surfacing teams that need attention before the ones that don't.
  const [sortDir, setSortDir] = useState<SortDir>("worst-first");
  const [compareIds, setCompareIds] = useState<string[]>([]);

  const dailyByStore = useMemo(() => {
    const map = new Map<string, TeamDailyActivity>();
    for (const t of data.teams) map.set(t.store, t);
    return map;
  }, [data.teams]);

  const toggleCompare = (store: string): void => {
    setCompareIds((prev) => {
      if (prev.includes(store)) return prev.filter((s) => s !== store);
      if (prev.length >= 2) return prev;
      return [...prev, store];
    });
  };

  const toggleTier = (tier: DailyActivityTier): void => {
    setSelectedTiers((prev) => {
      if (prev.includes(tier)) return prev.filter((t) => t !== tier);
      if (prev.length >= 2) return prev;
      return [...prev, tier];
    });
  };

  const tierCounts = useMemo(() => {
    const counts: Record<DailyActivityTier, number> = {
      highly_active: 0,
      occasional: 0,
      rare: 0,
      dark: 0,
    };
    for (const t of data.teams) counts[t.tier] += 1;
    return counts;
  }, [data.teams]);

  const teams = useMemo(() => {
    const q = query.trim().toLowerCase();
    let filtered = q ? data.teams.filter((t) => t.store.toLowerCase().includes(q)) : data.teams;
    if (selectedTiers.length === 1) filtered = filtered.filter((t) => t.tier === selectedTiers[0]);
    const sign = sortDir === "worst-first" ? 1 : -1;
    return [...filtered].sort((a, b) => sign * (a.l28 - b.l28));
  }, [data.teams, query, selectedTiers, sortDir]);

  // When exactly two tiers are selected, split the (search-filtered) teams
  // into one sorted list per tier so the two groups can sit side by side.
  const tierComparisonGroups = useMemo(() => {
    if (selectedTiers.length !== 2) return null;
    const q = query.trim().toLowerCase();
    const base = q ? data.teams.filter((t) => t.store.toLowerCase().includes(q)) : data.teams;
    const sign = sortDir === "worst-first" ? 1 : -1;
    return selectedTiers.map((tier) => ({
      tier,
      teams: base.filter((t) => t.tier === tier).sort((a, b) => sign * (a.l28 - b.l28)),
    }));
  }, [data.teams, query, selectedTiers, sortDir]);

  if (data.teams.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-4xl mb-4">📊</p>
        <p className="text-sm text-muted-foreground max-w-xs">
          No teams in this period yet. Upload a reporting period with team data to see this view.
        </p>
      </div>
    );
  }

  // Comparing two teams replaces the whole grid — the rest fall away so the
  // two selected cards can sit directly side by side, using the same card
  // used everywhere else in this tab.
  if (compareIds.length === 2 && compareIds[0] && compareIds[1]) {
    const teamA = dailyByStore.get(compareIds[0]);
    const teamB = dailyByStore.get(compareIds[1]);

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-head font-medium">
            Comparing {compareIds[0]} <span className="text-muted-foreground font-normal">vs</span>{" "}
            {compareIds[1]}
          </p>
          <button
            type="button"
            onClick={() => {
              setCompareIds([]);
            }}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground flex-shrink-0"
          >
            <X className="h-3.5 w-3.5" />
            Clear comparison
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {teamA && (
            <TeamActivityCard
              team={teamA}
              selected
              onToggleCompare={() => {
                toggleCompare(teamA.store);
              }}
            />
          )}
          {teamB && (
            <TeamActivityCard
              team={teamB}
              selected
              onToggleCompare={() => {
                toggleCompare(teamB.store);
              }}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-xs font-body text-muted-foreground">
        Each square is one day in the selected month. Filled = at least one qualifying device
        session (≥2 min) by any device on that team that day. L7 and L28 count filled days in the
        trailing 7 and 28 days of the month.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <TierFilterPill
          label="All teams"
          count={data.teams.length}
          color={null}
          active={selectedTiers.length === 0}
          onClick={() => {
            setSelectedTiers([]);
          }}
        />
        {TIER_ORDER.map((tier) => (
          <TierFilterPill
            key={tier}
            label={TIER_LABELS[tier]}
            count={tierCounts[tier]}
            color={TIER_COLORS[tier]}
            active={selectedTiers.includes(tier)}
            onClick={() => {
              toggleTier(tier);
            }}
          />
        ))}
      </div>

      {selectedTiers.length === 1 && selectedTiers[0] && (
        <p className="text-xs text-muted-foreground">
          Select another tier pill to compare {TIER_LABELS[selectedTiers[0]].toLowerCase()} teams
          against another tier.
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        {data.teams.length > 6 ? (
          <div className="relative max-w-xs flex-1 min-w-[180px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search teams…"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
              }}
              className="pl-8"
            />
          </div>
        ) : (
          <div />
        )}

        <div className="flex items-center gap-3">
          {!tierComparisonGroups && (
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {teams.length} of {data.teams.length} teams
            </span>
          )}
          <button
            type="button"
            onClick={() => {
              setSortDir((d) => (d === "worst-first" ? "best-first" : "worst-first"));
            }}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-head font-medium text-muted-foreground transition-colors hover:text-foreground hover:border-phoenix-orange whitespace-nowrap"
          >
            {sortDir === "worst-first" ? (
              <>
                <ArrowDownWideNarrow className="h-3.5 w-3.5" />
                Worst first
              </>
            ) : (
              <>
                <ArrowUpWideNarrow className="h-3.5 w-3.5" />
                Best first
              </>
            )}
          </button>
        </div>
      </div>

      {compareIds.length === 1 ? (
        <p className="text-xs text-muted-foreground">
          Select one more team's "Compare" checkbox to see them side by side.
        </p>
      ) : null}

      {tierComparisonGroups?.[0] && tierComparisonGroups[1] ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-head font-medium">
              Comparing {TIER_LABELS[tierComparisonGroups[0].tier]}{" "}
              <span className="text-muted-foreground font-normal">vs</span>{" "}
              {TIER_LABELS[tierComparisonGroups[1].tier]}
            </p>
            <button
              type="button"
              onClick={() => {
                setSelectedTiers([]);
              }}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground flex-shrink-0"
            >
              <X className="h-3.5 w-3.5" />
              Clear comparison
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {tierComparisonGroups.map(({ tier, teams: groupTeams }) => (
              <div key={tier} className="space-y-3">
                <div className="flex items-center gap-2">
                  <TierPill tier={tier} />
                  <span className="text-xs text-muted-foreground">
                    {groupTeams.length} team{groupTeams.length === 1 ? "" : "s"}
                  </span>
                </div>
                {groupTeams.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-8 text-center">
                    No {TIER_LABELS[tier].toLowerCase()} teams{query ? ` matching "${query}"` : ""}.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 gap-4">
                    {groupTeams.map((team) => (
                      <TeamActivityCard
                        key={team.store}
                        team={team}
                        selected={compareIds.includes(team.store)}
                        onToggleCompare={() => {
                          toggleCompare(team.store);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : teams.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">
          No {selectedTiers[0] ? `${TIER_LABELS[selectedTiers[0]].toLowerCase()} ` : ""}teams match
          {query ? ` "${query}"` : " your filters"}.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {teams.map((team) => (
            <TeamActivityCard
              key={team.store}
              team={team}
              selected={compareIds.includes(team.store)}
              onToggleCompare={() => {
                toggleCompare(team.store);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
